<template>
  <div class="ec-page">
    <!-- ── Header ─────────────────────────────────────────────── -->
    <div class="ec-header">
      <div class="ec-header__left">
        <div class="ec-header__icon">
          <v-icon size="22">mdi-gift-outline</v-icon>
        </div>
        <div>
          <h1 class="ec-header__title">Customer License Gifts</h1>
          <p class="ec-header__subtitle">Gift Cordatus licenses to past Openzeka customers — controlled, wave-by-wave delivery</p>
        </div>
      </div>
      <div class="ec-header__actions">
        <input ref="fileInput" type="file" accept=".csv" style="display:none" @change="onFile" />
        <button class="ec-btn ec-btn--ghost" @click="$refs.fileInput.click()">
          <v-icon size="14" style="margin-right:5px">mdi-file-delimited-outline</v-icon>
          {{ file ? shortName(file.name) : 'Select CSV' }}
        </button>
        <button class="ec-btn ec-btn--primary" :disabled="!file || importing" @click="doImport">
          <v-progress-circular v-if="importing" size="13" width="2" indeterminate style="margin-right:6px" />
          Import
        </button>
      </div>
    </div>

    <!-- ── Stat cards ─────────────────────────────────────────── -->
    <div class="gb-stats">
      <div v-for="c in statCards" :key="c.key" class="gb-stat" :class="`gb-stat--${c.key}`">
        <div class="gb-stat__n">{{ c.count.toLocaleString() }}</div>
        <div class="gb-stat__foot">
          <span class="gb-stat__label">{{ c.label }}</span>
          <span class="gb-stat__seat">{{ c.seats.toLocaleString() }} seats</span>
        </div>
      </div>
    </div>

    <!-- ── Filters ────────────────────────────────────────────── -->
    <div class="gb-card">
      <div class="gb-card__label">Filters</div>
      <div class="gb-row">
        <v-select v-model="filters.status" :items="statusOptions" label="Status" dense outlined hide-details clearable class="gb-field" @change="reload" />
        <v-select v-model="filters.licence_type" :items="typeOptions" label="License type" dense outlined hide-details clearable class="gb-field" @change="reload" />
        <v-select v-model="filters.year" :items="yearOptions" label="Year & later" dense outlined hide-details clearable class="gb-field" @change="reload" />
        <v-text-field v-model="filters.q" label="Search email" dense outlined hide-details clearable append-icon="mdi-magnify" class="gb-field gb-field--wide" @keyup.enter="reload" @click:append="reload" />
        <v-spacer />
        <v-switch v-model="filters.exclude_internal" label="Hide test / internal" dense hide-details inset class="gb-switch" @change="reload" />
      </div>
    </div>

    <!-- ── Send wave ──────────────────────────────────────────── -->
    <div class="gb-card gb-card--send">
      <div class="gb-send">
        <span class="gb-send__title">Send wave:</span>
        <label class="gb-fld gb-fld--sm">
          <span class="gb-fld__l">Batch</span>
          <input class="gb-input" type="number" v-model.number="wave.limit" />
        </label>
        <label class="gb-fld gb-fld--wide">
          <span class="gb-fld__l">Lock to single address (test)</span>
          <input class="gb-input" v-model="wave.test_only_email" placeholder="ozkan@openzeka.com" />
        </label>
        <label class="gb-chk"><input type="checkbox" v-model="wave.dry_run" /> Dry-run (preview only)</label>
        <span class="gb-flex" />
        <button class="ec-btn ec-btn--ghost" :disabled="sending" @click="send(true)">Dry-run</button>
        <button class="ec-btn ec-btn--primary gb-send-btn" :disabled="sending" @click="confirmDialog = true">{{ sending ? 'Sending…' : 'Send wave' }}</button>
      </div>
      <div class="gb-hint">Only <strong>pending</strong> recipients are processed. Dry-run sends nothing. Delivery is idempotent — a recipient is never emailed twice.</div>
    </div>

    <!-- ── Table ──────────────────────────────────────────────── -->
    <div class="ec-list">
      <v-data-table
        class="ec-table"
        :headers="headers" :items="items" :server-items-length="total"
        :options.sync="options" :loading="loading"
        :footer-props="{ 'items-per-page-options': [25,50,100,250] }"
      >
        <template #item.licence_configs="{ item }">
          <span class="gb-grants">{{ grantsText(item) }}</span>
        </template>
        <template #item.status="{ item }">
          <span class="u-status-pill" :class="pillClass(item.status)">{{ item.status }}</span>
        </template>
        <template #item.activation_code="{ item }">
          <code v-if="item.activation_code" class="gb-code">{{ item.activation_code }}</code>
          <span v-else class="gb-muted">—</span>
        </template>
        <template #item.is_internal="{ item }">
          <v-icon v-if="item.is_internal" size="16" color="#f59e0b" title="test / internal">mdi-alert-circle-outline</v-icon>
        </template>
      </v-data-table>
    </div>

    <!-- ── Confirm dialog ─────────────────────────────────────── -->
    <v-dialog v-model="confirmDialog" max-width="460">
      <div class="gb-confirm">
        <div class="gb-confirm__header">
          <div class="gb-confirm__icon"><v-icon size="20" color="white">mdi-send</v-icon></div>
          <h2 class="gb-confirm__title">Confirm real send</h2>
        </div>
        <div class="gb-confirm__body">
          A <strong>real email</strong> will be sent to <strong>pending</strong> recipients (up to {{ wave.limit }}).
          <template v-if="wave.test_only_email"><br>🔒 Locked to: <strong>{{ wave.test_only_email }}</strong></template>
          <template v-if="filters.exclude_internal"><br>Test / internal addresses excluded.</template>
        </div>
        <div class="gb-confirm__footer">
          <button class="ec-btn ec-btn--ghost" @click="confirmDialog = false">Cancel</button>
          <button class="ec-btn ec-btn--primary" @click="confirmDialog = false; send(false)">Yes, send</button>
        </div>
      </div>
    </v-dialog>

    <v-snackbar v-model="snack.show" :color="snack.color" :timeout="6000">{{ snack.text }}</v-snackbar>
  </div>
</template>

<script>
import { apiService } from '@/service/api.service'

const LIC_LABEL = { 2: 'Orin', 46: 'Thor', 47: 'Spark/DGX' }

export default {
  name: 'GiftBackfill',
  data () {
    return {
      file: null,
      importing: false,
      loading: false,
      sending: false,
      confirmDialog: false,
      total: 0,
      items: [],
      options: { page: 1, itemsPerPage: 50 },
      stats: { total: 0, by_status: {} },
      filters: { status: null, licence_type: null, year: null, q: '', exclude_internal: true },
      wave: { limit: 50, dry_run: true, test_only_email: '' },
      statusOptions: ['pending', 'sent', 'failed', 'skipped'],
      typeOptions: [{ text: 'Orin (2)', value: '2' }, { text: 'Thor (46)', value: '46' }, { text: 'Spark/DGX (47)', value: '47' }],
      yearOptions: ['2022', '2023', '2024', '2025', '2026'],
      headers: [
        { text: 'Email', value: 'email' },
        { text: 'Name', value: 'name' },
        { text: 'License', value: 'licence_configs', sortable: false },
        { text: 'Seats', value: 'total_seats' },
        { text: 'Year', value: 'year' },
        { text: 'Status', value: 'status' },
        { text: 'Code', value: 'activation_code' },
        { text: '', value: 'is_internal', sortable: false, width: 40 }
      ],
      snack: { show: false, color: 'success', text: '' }
    }
  },
  computed: {
    statCards () {
      const s = this.stats.by_status || {}
      const g = (k) => ({ count: (s[k] || {}).count || 0, seats: (s[k] || {}).seats || 0 })
      return [
        { key: 'total', label: 'Total', icon: 'mdi-account-multiple-outline', grad: 'linear-gradient(135deg,#6a5bf0,#5b6af0)', count: this.stats.total || 0, seats: Object.values(s).reduce((a, v) => a + (v.seats || 0), 0) },
        { key: 'pending', label: 'Pending', icon: 'mdi-clock-outline', grad: 'linear-gradient(135deg,#fbbf24,#f97316)', ...g('pending') },
        { key: 'sent', label: 'Sent', icon: 'mdi-check-circle-outline', grad: 'linear-gradient(135deg,#34d399,#15803d)', ...g('sent') },
        { key: 'failed', label: 'Failed', icon: 'mdi-alert-circle-outline', grad: 'linear-gradient(135deg,#f87171,#dc2626)', ...g('failed') }
      ]
    }
  },
  watch: { options: { handler () { this.loadRecipients() }, deep: true } },
  mounted () { this.loadStats(); this.loadRecipients() },
  methods: {
    shortName (n) { return n.length > 22 ? n.slice(0, 20) + '…' : n },
    grantsText (item) {
      return (item.licence_configs || []).map(c => `${c.seats} × ${LIC_LABEL[c.licence_type_id] || c.licence_type_id}`).join(' + ')
    },
    pillClass (s) {
      return { pending: 'u-pill--amber', sent: 'u-pill--green', failed: 'u-pill--red', skipped: 'u-pill--gray' }[s] || 'u-pill--gray'
    },
    onFile (e) { this.file = (e.target.files || [])[0] || null },
    reload () { this.options.page = 1; this.loadRecipients(); this.loadStats() },
    async loadStats () {
      try { const res = await apiService.get('admin-gift-backfill/stats'); this.stats = res.data } catch (e) { /* noop */ }
    },
    async loadRecipients () {
      this.loading = true
      try {
        const p = new URLSearchParams()
        p.append('page', this.options.page)
        p.append('per_page', this.options.itemsPerPage)
        if (this.filters.status) p.append('status', this.filters.status)
        if (this.filters.licence_type) p.append('licence_type', this.filters.licence_type)
        if (this.filters.year) p.append('year', this.filters.year)
        if (this.filters.q) p.append('q', this.filters.q)
        if (this.filters.exclude_internal) p.append('exclude_internal', '1')
        const res = await apiService.get(`admin-gift-backfill/recipients?${p.toString()}`)
        this.items = res.data.items; this.total = res.data.total
      } catch (e) { this.notify('Failed to load list', 'error') } finally { this.loading = false }
    },
    async doImport () {
      this.importing = true
      try {
        const fd = new FormData(); fd.append('file', this.file)
        const res = await apiService.post('admin-gift-backfill/import', fd, { headers: { 'Content-Type': undefined } })
        const d = res.data
        this.notify(`Imported: ${d.inserted} added, ${d.skipped} skipped${d.no_email ? ' — ' + d.no_email + ' rows without email!' : ''}`, d.inserted ? 'success' : 'error')
        this.file = null; this.reload()
      } catch (e) { this.notify('Import error: ' + (e.response?.data?.message || e.message), 'error') } finally { this.importing = false }
    },
    async send (dryRun) {
      this.sending = true
      try {
        const body = {
          limit: this.wave.limit,
          dry_run: dryRun,
          exclude_internal: this.filters.exclude_internal,
          test_only_email: this.wave.test_only_email || undefined,
          licence_type: this.filters.licence_type || undefined,
          year_min: this.filters.year || undefined
        }
        const res = await apiService.post('admin-gift-backfill/send', body)
        const d = res.data
        this.notify(`${dryRun ? 'DRY-RUN (nothing sent)' : 'SENT'} — processed ${d.processed}, ${dryRun ? 'would send' : 'succeeded'} ${d.sent}, failed ${d.failed}`, dryRun ? 'info' : 'success')
        if (!dryRun) this.reload()
      } catch (e) { this.notify('Send error: ' + (e.response?.data?.message || e.message), 'error') } finally { this.sending = false }
    },
    notify (text, color) {
      this.snack = { show: true, color: color === 'error' ? '#dc2626' : color === 'info' ? '#475569' : '#15803d', text }
    }
  }
}
</script>

<style scoped>
/* Stat cards (flat tinted) */
.gb-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin: 18px 0; }
.gb-stat { background: #eef2ff; border: 1px solid #dbe4fb; border-radius: 12px; padding: 14px 18px; }
.gb-stat__n { font-size: 1.7rem; font-weight: 800; line-height: 1; color: #2563eb; font-variant-numeric: tabular-nums; }
.gb-stat__foot { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; margin-top: 8px; }
.gb-stat__label { font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: #64748b; }
.gb-stat__seat { font-size: .68rem; font-weight: 600; text-transform: uppercase; letter-spacing: .3px; color: #aab4c5; }
.gb-stat--pending { background: #fff7ed; border-color: #fde3c2; }
.gb-stat--pending .gb-stat__n { color: #d97706; }
.gb-stat--sent { background: #f0fdf4; border-color: #c7ecd2; }
.gb-stat--sent .gb-stat__n { color: #16a34a; }
.gb-stat--failed { background: #fef2f2; border-color: #fbd5d5; }
.gb-stat--failed .gb-stat__n { color: #dc2626; }

/* Control cards (Filters uses these) */
.gb-card { background: #fff; border: 1px solid #e8ebf0; border-radius: 12px; padding: 16px 18px 14px; margin-bottom: 16px; }
.gb-card--send { padding: 16px 18px; }
.gb-card__label { display: flex; align-items: center; font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .8px; color: #94a3b8; margin-bottom: 12px; }
.gb-row { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; }
.gb-field { max-width: 180px; }
.gb-field--wide { max-width: 300px; flex: 1; min-width: 200px; }
.gb-field--sm { max-width: 110px; }
.gb-switch { margin-top: 0 !important; padding-top: 0 !important; }

/* Send wave (flat horizontal) */
.gb-send { display: flex; flex-wrap: wrap; gap: 14px; align-items: flex-end; }
.gb-send__title { font-size: .85rem; font-weight: 700; color: #1f2937; align-self: center; }
.gb-fld { display: flex; flex-direction: column; gap: 5px; }
.gb-fld--sm { width: 90px; }
.gb-fld--wide { flex: 1; min-width: 240px; }
.gb-fld__l { font-size: .72rem; font-weight: 600; color: #64748b; }
.gb-input { border: 1px solid #cfd6e4; border-radius: 8px; padding: 7px 11px; font-size: .84rem; width: 100%; }
.gb-input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.12); }
.gb-chk { font-size: .82rem; color: #34405a; display: flex; align-items: center; gap: 6px; cursor: pointer; align-self: center; }
.gb-flex { flex: 1; }
.gb-send-btn { min-width: 120px; justify-content: center; }
.gb-hint { margin-top: 12px; font-size: .76rem; color: #94a3b8; line-height: 1.5; }
.gb-hint strong { color: #64748b; }

/* Table cells */
.gb-grants { font-size: .8rem; color: #42526e; }
.gb-code { font-family: 'SF Mono', Menlo, Consolas, monospace; font-size: .76rem; background: #f1f5f9; padding: 2px 6px; border-radius: 4px; color: #0f172a; }
.gb-muted { color: #cbd5e1; }

/* Confirm dialog */
.gb-confirm { background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 20px 60px rgba(0,0,0,.18); }
.gb-confirm__header { display: flex; align-items: center; gap: 12px; padding: 18px 20px; background: #ecfdf5; border-bottom: 1px solid #bbf7d0; }
.gb-confirm__icon { width: 34px; height: 34px; border-radius: 50%; background: #22c55e; display: flex; align-items: center; justify-content: center; }
.gb-confirm__title { font-size: .95rem; font-weight: 700; color: #15803d; margin: 0; }
.gb-confirm__body { padding: 20px; font-size: .88rem; color: #42526e; line-height: 1.6; }
.gb-confirm__footer { display: flex; justify-content: flex-end; gap: 8px; padding: 16px 20px; border-top: 1px solid #f1f2f4; }
</style>
